import {
  useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { useOverlay } from './overlays.js'
import { usePresence } from './presence.js'

/**
 * FLOATING UI (LOT 5.5.1) — le socle commun de tout contenu flottant.
 *
 * RÈGLE ABSOLUE (design system) : un contenu flottant ne sort JAMAIS du
 * viewport. Chaque surface montée ici obtient :
 *
 *   · un PORTAL vers <body> — aucun parent `overflow: hidden` ne peut la
 *     découper, aucun contexte de stacking ne peut la piéger ;
 *   · FLIP vertical — pas la place dessous → elle s'ouvre au-dessus ;
 *   · SHIFT horizontal — près d'un bord → elle se décale pour rester visible ;
 *   · max-height calculée sur l'espace réel + scroll interne.
 *
 * Le repositionnement suit le scroll et le resize tant que la surface est
 * ouverte (position: fixed → coordonnées viewport, pas document).
 */

const VIEWPORT_PAD = 8
const GAP = 6

export type FloatingPlacement = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'

interface Position { top: number; left: number; maxHeight: number; placedAbove: boolean }

function computePosition(
  anchor: DOMRect, panel: { width: number; height: number },
  placement: FloatingPlacement,
): Position {
  const vw = window.innerWidth
  const vh = window.innerHeight

  const spaceBelow = vh - anchor.bottom - GAP - VIEWPORT_PAD
  const spaceAbove = anchor.top - GAP - VIEWPORT_PAD
  const wantAbove = placement.startsWith('top')

  // FLIP : on respecte la préférence, sauf si l'espace manque ET que l'autre
  // côté en offre davantage.
  let above = wantAbove
  const fits = (space: number) => panel.height <= space
  if (wantAbove && !fits(spaceAbove) && spaceBelow > spaceAbove) above = false
  if (!wantAbove && !fits(spaceBelow) && spaceAbove > spaceBelow) above = true

  const maxHeight = Math.max(120, above ? spaceAbove : spaceBelow)
  const height = Math.min(panel.height, maxHeight)
  let top = above ? anchor.top - GAP - height : anchor.bottom + GAP

  /*
   * RABATTEMENT VERTICAL — la règle « jamais hors du viewport » ne valait
   * jusqu'ici que pour la largeur.
   *
   * Constaté en recette : une ancre poussée hors de sa zone défilante (le
   * corps d'une modale qu'on a fait défiler) a un `top` NÉGATIF, et le
   * panneau la suivait au-dessus du bord de l'écran. Le calcul de place
   * suppose une ancre visible ; le rabattement ne le suppose pas.
   */
  top = Math.min(top, vh - height - VIEWPORT_PAD)
  top = Math.max(top, VIEWPORT_PAD)

  // SHIFT : alignement start/end, puis rabattement dans le viewport.
  let left = placement.endsWith('end') ? anchor.right - panel.width : anchor.left
  left = Math.min(left, vw - panel.width - VIEWPORT_PAD)
  left = Math.max(left, VIEWPORT_PAD)

  return { top, left, maxHeight, placedAbove: above }
}

export function Floating(props: {
  open: boolean
  anchorRef: React.RefObject<HTMLElement | null>
  children: ReactNode
  placement?: FloatingPlacement
  /** Largeur minimale = largeur de l'ancre (selects). */
  matchAnchorWidth?: boolean
  className?: string
  role?: string
  id?: string
  ariaLabel?: string
  /** Fermée par un clic hors ancre + hors panneau. */
  onOutside?: (() => void) | undefined
  /**
   * ANCRE À UN POINT VIRTUEL (menu contextuel) — la surface se ferme au
   * défilement et au redimensionnement.
   *
   * Défaut `false` : un popover ancré à un VRAI élément suit son ancre
   * (`reposition`), il n'a aucune raison de disparaître quand la page défile.
   * Une ancre synthétique (`floatingAnchor(x, y)`) a des coordonnées viewport
   * FIGÉES : au défilement elle ne bouge plus avec la cellule qu'elle
   * désigne, et le menu devient un menu fantôme qui agit sur une cible qui
   * n'est plus sous lui. Dans ce cas la seule réponse honnête est de fermer.
   */
  closeOnScroll?: boolean | undefined
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  const [style, setStyle] = useState<CSSProperties>({ visibility: 'hidden' })
  // Présence (LOT 5.5.3) : la surface reste montée ~140 ms pour jouer sa
  // sortie — même contrat que les modales (data-state="closing"). Déclarée
  // AVANT l'effet de positionnement : au premier open, le panneau n'existe
  // qu'au rendu suivant — l'effet doit re-tourner quand `mounted` bascule.
  const presence = usePresence(props.open, 140)

  /*
   * ESCAPE — la surface s'inscrit dans la PILE d'overlays.
   *
   * Sans cette inscription, une liste ouverte depuis une modale n'existait
   * pas pour l'arbitre : Escape trouvait la modale au sommet et fermait
   * TOUT, liste et modale, alors que l'utilisateur ne voulait renoncer qu'au
   * menu. Aucun verrou de défilement ici — une liste déroulante n'a pas à
   * figer la page derrière elle.
   */
  useOverlay(props.open && props.onOutside !== undefined, () => props.onOutside?.())

  useLayoutEffect(() => {
    if (!props.open) return
    const reposition = () => {
      const anchor = props.anchorRef.current?.getBoundingClientRect()
      const panel = panelRef.current
      if (!anchor || !panel) return
      // Mesure hors contrainte (max-height retirée le temps de la mesure).
      panel.style.maxHeight = ''
      const rect = panel.getBoundingClientRect()
      const pos = computePosition(anchor, { width: rect.width, height: rect.height }, props.placement ?? 'bottom-start')
      setStyle({
        position: 'fixed', top: pos.top, left: pos.left,
        maxHeight: pos.maxHeight, visibility: 'visible',
        minWidth: props.matchAnchorWidth ? anchor.width : undefined,
      })
    }
    reposition()
    const ro = new ResizeObserver(reposition)
    if (panelRef.current) ro.observe(panelRef.current)
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      ro.disconnect()
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [props.open, presence.mounted, props.placement, props.matchAnchorWidth]) // eslint-disable-line react-hooks/exhaustive-deps

  /*
   * FERMETURE PAR L'EXTÉRIEUR — clic gauche ET clic droit.
   *
   * BUG CORRIGÉ (LOT 4.8) : ce handler faisait
   * `props.anchorRef.current?.contains(t)`. L'optional chaining ne protège
   * que d'une ancre ABSENTE, pas d'une ancre qui n'est pas un nœud DOM :
   * `floatingAnchor(x, y)` (menu contextuel du planning) est un objet nu qui
   * ne porte que `getBoundingClientRect`. `contains` y valait `undefined`,
   * l'appel levait une TypeError DANS le handler, et la ligne suivante —
   * `props.onOutside?.()` — n'était JAMAIS atteinte : le menu contextuel ne
   * se fermait plus au clic extérieur. D'où la garde de type explicite.
   *
   * `contextmenu` est écouté en phase de CAPTURE, à dessein : un clic droit
   * ailleurs doit REMPLACER la surface, pas en empiler une seconde. En
   * capture, la fermeture s'exécute avant le gestionnaire React de la
   * nouvelle cible, donc dans l'ordre « je ferme l'ancien, tu ouvres le
   * nouveau ». En phase de bulle, l'ordre s'inverserait et la fermeture
   * annulerait le menu qui vient de s'ouvrir.
   */
  useLayoutEffect(() => {
    if (!props.open || !props.onOutside) return
    const onDoc = (e: Event) => {
      const t = e.target as Node
      if (panelRef.current?.contains(t)) return
      const a = props.anchorRef.current
      if (a && typeof a.contains === 'function' && a.contains(t)) return
      props.onOutside?.()
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('contextmenu', onDoc, true)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('contextmenu', onDoc, true)
    }
  }, [props.open]) // eslint-disable-line react-hooks/exhaustive-deps

  /* Fermeture au défilement / redimensionnement, sur demande explicite. */
  useLayoutEffect(() => {
    if (!props.open || !props.closeOnScroll || !props.onOutside) return
    // Défiler LE MENU n'est pas défiler DERRIÈRE lui. `.lc-floating` est
    // `overflow-y: auto` et plafonné à la place disponible : un menu de six
    // entrées défile dès qu'on l'ouvre en bas d'un écran court, et le coup de
    // molette pour atteindre « Supprimer » le fermait au lieu de le faire
    // défiler. Même garde que pour le clic extérieur.
    const close = (e: Event) => {
      const t = e.target
      if (t instanceof Node && panelRef.current?.contains(t)) return
      props.onOutside?.()
    }
    // Capture : un `scroll` ne remonte pas, mais il DESCEND — c'est ainsi
    // qu'on entend défiler un conteneur interne (la grille du planning).
    window.addEventListener('scroll', close, { capture: true, passive: true })
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', close, { capture: true })
      window.removeEventListener('resize', close)
    }
  }, [props.open, props.closeOnScroll]) // eslint-disable-line react-hooks/exhaustive-deps

  if (!presence.mounted) return null
  return createPortal(
    <div
      ref={panelRef}
      id={props.id}
      role={props.role}
      aria-label={props.ariaLabel}
      data-state={presence.state}
      className={`lc-floating${props.className ? ` ${props.className}` : ''}`}
      style={style}
    >
      {props.children}
    </div>,
    document.body,
  )
}
